import { useEffect, useState } from 'react';

/** 面板開啟／關閉、通知滑入的時長。關比開快，體感更俐落 */
export const PANEL_IN_MS = 260;
export const PANEL_OUT_MS = 200;
export const NOTIFY_IN_MS = 220;
/** 拖動放開後（例如拖出畫面被夾回）補間到最終位置的時長 */
export const SETTLE_MS = 220;

/** launcher 按下／放開的回饋時長。要比開關面板短很多，否則手感是「黏」而不是「彈」 */
export const PRESS_MS = 130;

export const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';
export const EASE_IN = 'cubic-bezier(0.4, 0, 1, 1)';

/**
 * 使用者是否偏好減少動態效果。
 *
 * 無障礙基本要求，不列為可省 —— 偵測到就把所有轉場時長歸零、元素直接就位。
 */
export const useReducedMotion = (): boolean => {
	const [reduced, setReduced] = useState(() => {
		try {
			return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		} catch (e) {
			return false;
		}
	});

	useEffect(() => {
		let mq: MediaQueryList;
		try {
			mq = window.matchMedia('(prefers-reduced-motion: reduce)');
		} catch (e) {
			return undefined;
		}

		const sync = () => setReduced(mq.matches);
		// Safari 14 以前只有 addListener
		if (mq.addEventListener) {
			mq.addEventListener('change', sync);
			return () => mq.removeEventListener('change', sync);
		}
		mq.addListener(sync);
		return () => mq.removeListener(sync);
	}, []);

	return reduced;
};

export interface Presence {
	/** 是否要留在 DOM 裡（關閉動畫期間仍為 true） */
	mounted: boolean;
	/** 是否已進入「開啟」的視覺狀態；false 代表在進場前或退場中 */
	entered: boolean;
}

/**
 * 讓元素在關閉時能播完退場動畫才卸載。
 *
 * 順帶決定熱區的收回時機 —— 呼叫端用 `mounted` 而不是原本的開關旗標去算可互動區域，
 * 關閉動畫進行中面板區域就還不會穿透，動畫結束才交還宿主頁。
 */
export const usePresence = (active: boolean, exitMs: number): Presence => {
	const [mounted, setMounted] = useState(active);
	const [entered, setEntered] = useState(active);

	useEffect(() => {
		if (active) {
			setMounted(true);

			/**
			 * 必須等「起始狀態真的被畫出來一次」才切到 entered，否則沒有可補間的起點。
			 *
			 * 單層 rAF 不夠：rAF 是在繪製*之前*執行的，而 legacy root 的 setState 在
			 * rAF 裡是同步 re-render —— 於是起始值還沒被畫就被改成終值，第一次繪製就已經
			 * 是最終樣子，動畫完全不會發生。所以要雙層 rAF：第一層在 render 的繪製前，
			 * 第二層才落在那次繪製之後。
			 *
			 * 另外 rAF 在 `document.hidden` 時完全不觸發（背景分頁呼叫 openIcon()、或背景
			 * 分頁收到通知），所以再排一個 timeout 兜底 —— 隱藏時本來也不會有動畫，它的
			 * 職責只是確保 entered 最終會翻過去，元素不會卡在起始狀態。
			 */
			let inner = 0;
			const outer = requestAnimationFrame(() => {
				inner = requestAnimationFrame(() => setEntered(true));
			});
			const fallback = setTimeout(() => setEntered(true), 60);
			return () => {
				cancelAnimationFrame(outer);
				cancelAnimationFrame(inner);
				clearTimeout(fallback);
			};
		}

		setEntered(false);
		if (exitMs <= 0) {
			setMounted(false);
			return undefined;
		}

		const timer = setTimeout(() => setMounted(false), exitMs);
		return () => clearTimeout(timer);
	}, [active, exitMs]);

	return { mounted, entered };
};
